import { findParentNodeClosestToPos } from '@tiptap/core';
import type { Transaction } from '@tiptap/pm/state';

import { TIPTAP_LAYOUT_CELL_TYPE, TIPTAP_LAYOUT_TYPE } from './../../ricos-content';
import type { ExtensionProps, NodeConfig, RicosExtension } from './../../ricos-types';
import { findNodeById } from './../../tiptap-editor/helpers';
import type { TiptapAdapterServices } from './../../tiptap-editor/initializeTiptapAdapter';
import { createRicosNodeExtension } from './../../tiptap-extensions';

import { cellNodeView } from './cell-node-view';
import { createEmptyCell, MAX_CELLS, MAX_COLUMNS } from './shared';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    [TIPTAP_LAYOUT_CELL_TYPE]: {
      selectCell: (nodeId: string) => ReturnType;
      deleteCell: (nodeId: string) => ReturnType;
      addCellLeft: (nodeId: string) => ReturnType;
      addCellRight: (nodeId: string) => ReturnType;
      moveCellLeft: (nodeId: string) => ReturnType;
      moveCellRight: (nodeId: string) => ReturnType;
    };
  }
}

const canAddCells = (tr: Transaction) => {
  const layoutNode = findParentNodeClosestToPos(
    tr.selection.$from,
    node => node.type?.name === TIPTAP_LAYOUT_TYPE
  )?.node;

  if (!layoutNode) {
    return false;
  }

  return layoutNode.childCount < MAX_CELLS;
};

const getSiblingNode = (tr: Transaction, nodeId: string, direction: 'left' | 'right') => {
  const parentNodeId = getParentNodeId(tr, nodeId);
  const parentNodeWithPos = findNodeById(tr, parentNodeId)[0];
  if (!parentNodeWithPos || parentNodeWithPos.node.childCount === 1) {
    return null;
  }

  let currentNodeIndex = -1;
  parentNodeWithPos.node.forEach((node, _offset, index) => {
    if (node.attrs.id === nodeId) {
      currentNodeIndex = index;
    }
  });

  if (currentNodeIndex < 0) {
    return null;
  }

  if (direction === 'left' && currentNodeIndex === 0) {
    return null;
  }

  if (direction === 'right' && currentNodeIndex === parentNodeWithPos.node.childCount - 1) {
    return null;
  }

  return parentNodeWithPos.node.child(currentNodeIndex + (direction === 'left' ? -1 : 1));
};

const getParentNodeId = (tr: Transaction, nodeId: string) => {
  const nodeWithPos = findNodeById(tr, nodeId)[0];
  const $pos = tr.doc.resolve(nodeWithPos.pos);
  return $pos.parent.attrs.id;
};

export const cellExtension = createRicosNodeExtension({
  groups: ['custom-styles'],
  type: 'html-node',
  name: TIPTAP_LAYOUT_CELL_TYPE,
  reconfigure: (
    config: NodeConfig,
    _extensions: RicosExtension[],
    _props: ExtensionProps,
    _settings: Record<string, unknown>,
    services: TiptapAdapterServices
  ) => {
    return {
      ...config,
      draggable: !services.context.isMobile,
    };
  },
  createExtensionConfig() {
    return {
      name: this.name,
      group: 'block',
      isolating: true,
      selectable: true,
      draggable: true,
      content: '(PARAGRAPH|layoutCellContent)*',
      parseHTML() {
        return [
          {
            tag: 'div[data-layout-cell]',
            getAttrs: (node: HTMLElement) => {
              const colSpan = Number(
                node.getAttribute('data-layout-cell-colspan') ?? MAX_COLUMNS / MAX_CELLS
              );
              return {
                colSpan: isNaN(colSpan) ? MAX_COLUMNS / MAX_CELLS : colSpan,
              };
            },
          },
        ];
      },
      renderHTML({ node }) {
        return [
          'div',
          { 'data-layout-cell': true, 'data-layout-cell-colspan': node.attrs.colSpan },
          0,
        ];
      },
      addNodeView() {
        return cellNodeView.bind(this);
      },
      addAttributes: () => ({
        colSpan: null,
      }),
      addCommands() {
        return {
          selectCell:
            (nodeId: string) =>
            ({ commands, tr }) => {
              const nodeWithPos = findNodeById(tr, nodeId)[0];
              return commands.setNodeSelection(nodeWithPos.pos);
            },
          deleteCell:
            (nodeId: string) =>
            ({ tr, chain }) => {
              const nodeWithPos = findNodeById(tr, nodeId)[0];
              const parentNodeId = getParentNodeId(tr, nodeId);
              const parentNodeWithPos = findNodeById(tr, parentNodeId)[0];

              if (parentNodeWithPos.node.childCount === 1) {
                return chain()
                  .deleteRange({
                    from: parentNodeWithPos.pos,
                    to: parentNodeWithPos.pos + parentNodeWithPos.node.nodeSize,
                  })
                  .run();
              }

              return chain()
                .deleteRange({
                  from: nodeWithPos.pos,
                  to: nodeWithPos.pos + nodeWithPos.node.nodeSize,
                })
                .balanceLayoutCells(parentNodeId)
                .focus()
                .run();
            },
          addCellLeft:
            (nodeId: string) =>
            ({ tr, dispatch, chain }) => {
              if (!dispatch) {
                return canAddCells(tr);
              }

              const nodeWithPos = findNodeById(tr, nodeId)[0];
              const parentNodeId = getParentNodeId(tr, nodeId);

              return chain()
                .focus()
                .insertContentAt(nodeWithPos.pos, createEmptyCell(3))
                .balanceLayoutCells(parentNodeId)
                .run();
            },
          addCellRight:
            (nodeId: string) =>
            ({ tr, dispatch, chain }) => {
              if (!dispatch) {
                return canAddCells(tr);
              }

              const nodeWithPos = findNodeById(tr, nodeId)[0];
              const parentNodeId = getParentNodeId(tr, nodeId);

              return chain()
                .focus()
                .insertContentAt(nodeWithPos.pos + nodeWithPos.node.nodeSize, createEmptyCell(3))
                .balanceLayoutCells(parentNodeId)
                .run();
            },
          moveCellLeft:
            (nodeId: string) =>
            ({ tr, dispatch, chain }) => {
              const nodeWithPos = findNodeById(tr, nodeId)[0];
              const nodeBefore = getSiblingNode(tr, nodeId, 'left');

              if (!nodeBefore) {
                return false;
              }

              const nodeBeforeWithPos = findNodeById(tr, nodeBefore.attrs.id)[0];

              if (dispatch) {
                return chain()
                  .focus()
                  .cut(
                    { from: nodeWithPos.pos, to: nodeWithPos.pos + nodeWithPos.node.nodeSize },
                    nodeBeforeWithPos.pos
                  )
                  .selectCell(nodeId)
                  .run();
              }

              return true;
            },
          moveCellRight:
            (nodeId: string) =>
            ({ tr, dispatch, chain }) => {
              const nodeWithPos = findNodeById(tr, nodeId)[0];
              const nodeAfter = getSiblingNode(tr, nodeId, 'right');

              if (!nodeAfter) {
                return false;
              }

              const nodeAfterWithPos = findNodeById(tr, nodeAfter.attrs.id)[0];

              if (dispatch) {
                return chain()
                  .focus()
                  .cut(
                    { from: nodeWithPos.pos, to: nodeWithPos.pos + nodeWithPos.node.nodeSize },
                    nodeAfterWithPos.pos + nodeAfterWithPos.node.nodeSize
                  )
                  .selectCell(nodeId)
                  .run();
              }

              return true;
            },
        };
      },
    };
  },
});
